// packages/frontend/src/features/meal-plans/planLinkage.ts
//
// Plan 14 (Daily Surfaces): closes the plan <-> cook loop on the client.
// `detectPlanRef` lets a cook session started FROM the recipe library (not from
// a plan slot tap) figure out whether today's plan already has this recipe
// queued up, so the session can still carry `planRef` for stats/UX purposes.
// `markSlotCooked` is the inverse: once a linked session completes, stamp the
// originating slot's `cookedAt` so the plan surface can show it as done.
import {
  localDateInZone,
  mondayOf,
  type MealType,
} from "@recipevault/shared";
import { db } from "../../sync/db.js";
import { editWeek } from "./editWeek.js";

export interface PlanRef {
  weekStartDate: string;
  slotId: string;
}

function nowIso(): string {
  return new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
}

// Priority order when a recipe appears in more than one of today's slots —
// dinner is the surface's default meal, so it wins ties. NO ambiguity prompt
// (couple-scale): the first match wins silently.
const MEAL_PRIORITY: readonly MealType[] = ["dinner", "lunch", "breakfast"];

/**
 * Best-effort link from a recipe being cooked back to today's plan slot that
 * queued it, if any. Reads Dexie only (no network) — mirrors `useTonight`'s
 * "what day/week is it" derivation (`localDateInZone` + `mondayOf`).
 *
 * Returns null when there's no plan row for this week, the row is
 * soft-deleted, today isn't represented, or the recipe isn't in any of
 * today's slots.
 */
export async function detectPlanRef(recipeId: string, tz: string): Promise<PlanRef | null> {
  const today = localDateInZone(tz, new Date());
  const weekStartDate = mondayOf(today);

  const plan = await db.mealPlans.get(weekStartDate);
  if (!plan || plan.deletedAt) return null;

  const day = plan.days.find((d) => d.date === today);
  if (!day) return null;

  for (const mealType of MEAL_PRIORITY) {
    const slot = day.slots.find(
      (s) =>
        s.mealType === mealType &&
        s.items.some((item) => item.kind === "recipe" && item.recipeId === recipeId),
    );
    if (slot) return { weekStartDate, slotId: slot.id };
  }

  return null;
}

/**
 * Stamps `cookedAt` on the plan slot a completed cook session was linked to,
 * via `editWeek` (whole-document LWW — no per-field merge, no new endpoint).
 *
 * The slot is addressed by id, scanning every day in the week rather than a
 * (date, mealType) position: unlike `editWeek`'s other producers, the caller
 * here only has the id captured on the session at start time, not a
 * date/mealType pair.
 *
 * Silently no-ops if the week or slot has vanished since the session
 * started (week deleted/never synced, or the slot id no longer exists in it)
 * — completion must never surface a plan-state error to the user.
 */
export async function markSlotCooked(planRef: PlanRef): Promise<void> {
  // Direct no-op for the absent/soft-deleted week: mirror `detectPlanRef`'s
  // guard. Without this, `editWeek` → `getOrCreateWeek` would (a) lazy-CREATE
  // a fresh empty week for an absent row, or (b) transiently mutate a
  // soft-deleted document's `days` and fire a PATCH the backend rejects.
  // Neither is correct — a vanished/deleted plan should be left untouched.
  const row = await db.mealPlans.get(planRef.weekStartDate);
  if (!row || row.deletedAt) return;

  await editWeek(planRef.weekStartDate, (days) => {
    for (const day of days) {
      const slot = day.slots.find((s) => s.id === planRef.slotId);
      if (slot) {
        slot.cookedAt = nowIso();
        break;
      }
    }
    return days;
  });
}
